/**
 * 统一的错误处理机制
 * 在Manager层集中处理所有错误，提供统一的错误处理接口
 */

import { useMemoStore } from '../../stores/memoStore';
import { useSectionStore } from '../../stores/sectionStore';
import { useGroupStore } from '../../stores/groupStore';
import { useTagStore } from '../../stores/tagStore';
import { useMemoObjectStore } from '../../stores/memoObjectStore';
import { useMemoIndexStore } from '../../stores/memoIndexStore';
import { useAppStore } from '../../stores/appStore';

// 错误类型定义
export type ErrorType = 'network' | 'validation' | 'server' | 'auth' | 'rate_limit' | 'unknown';

export interface AppError {
  type: ErrorType;
  message: string;
  code?: number;
  timestamp: number;
  context?: string;
  retryAfter?: number;
}

// 错误处理配置
export interface ErrorHandlerConfig {
  showToast?: boolean;
  logToConsole?: boolean;
  retryable?: boolean;
  context?: string;
}

// 429 Rate Limit 相关配置
const RATE_LIMIT_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 30000,
  toastDisplayTimeMs: 5000
};

// 全局429状态追踪
let rateLimitState = {
  isRateLimited: false,
  retryAt: 0,
  lastError: null as AppError | null
};

/**
 * 创建错误对象
 */
export const createError = (
  message: string,
  type: ErrorType = 'unknown',
  code?: number,
  context?: string,
  retryAfter?: number
): AppError => ({
  type,
  message,
  code,
  timestamp: Date.now(),
  context,
  retryAfter
});

/**
 * 检查是否为429 Rate Limit错误
 */
export const isRateLimitError = (error: any): boolean => {
  if (error?.code === 429) return true;
  if (error?.message?.includes('429')) return true;
  if (error?.message?.includes('Too Many Requests')) return true;
  if (error?.message?.includes('rate_limit_exceeded')) return true;
  return false;
};

/**
 * 获取429错误的等待时间（毫秒）
 */
export const getRateLimitWaitTime = (error: any): number => {
  if (error?.retryAfter) return error.retryAfter * 1000;
  
  // 基于重试次数计算指数退避
  const retryCount = error?.retryCount || 0;
  return Math.min(
    RATE_LIMIT_CONFIG.baseDelayMs * Math.pow(2, retryCount),
    RATE_LIMIT_CONFIG.maxDelayMs
  );
};

/**
 * 统一错误处理函数
 */
export const handleError = (
  error: any,
  config: ErrorHandlerConfig = {}
): AppError => {
  const { 
    showToast = true, 
    logToConsole = true, 
    retryable = false,
    context = 'unknown'
  } = config;

  // 解析错误信息
  let errorType: ErrorType = 'unknown';
  let errorMessage = '发生未知错误';
  let errorCode: number | undefined;
  let retryAfter: number | undefined;

  if (typeof error === 'object' && error !== null) {
    if (error instanceof Response) {
      errorCode = error.status;
      errorMessage = `HTTP ${error.status}: ${error.statusText}`;
      
      switch (error.status) {
        case 429:
          errorType = 'rate_limit';
          errorMessage = '请求过于频繁，请稍后再试';
          const retryAfterHeader = error.headers?.get('Retry-After');
          if (retryAfterHeader) {
            retryAfter = parseInt(retryAfterHeader, 10);
          }
          break;
        case 401:
        case 403:
          errorType = 'auth';
          errorMessage = '认证已失效，请重新登录';
          break;
        case 400:
          errorType = 'validation';
          errorMessage = '请求参数无效';
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          errorType = 'server';
          errorMessage = '服务器暂时不可用，请稍后重试';
          break;
        default:
          if (error.status >= 400 && error.status < 500) {
            errorType = 'validation';
          } else if (error.status >= 500) {
            errorType = 'server';
          }
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
      
      if (isRateLimitError(error)) {
        errorType = 'rate_limit';
        errorMessage = '请求过于频繁，请稍后再试';
        retryAfter = getRateLimitWaitTime(error);
      } else if (errorMessage.includes('网络') || errorMessage.includes('Network') || errorMessage.includes('fetch')) {
        errorType = 'network';
        errorMessage = '网络连接失败，请检查网络设置';
      } else if (errorMessage.includes('验证') || errorMessage.includes('Validation')) {
        errorType = 'validation';
      } else if (errorMessage.includes('服务器') || errorMessage.includes('Server')) {
        errorType = 'server';
      } else if (errorMessage.includes('认证') || errorMessage.includes('Auth')) {
        errorType = 'auth';
      }
    } else if (error?.message) {
      errorMessage = error.message;
      errorCode = error.code;
      if (errorCode === 429) {
        errorType = 'rate_limit';
        retryAfter = error.retryAfter;
      }
    }
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  const appError = createError(errorMessage, errorType, errorCode, context, retryAfter);

  // 更新全局429状态
  if (errorType === 'rate_limit') {
    rateLimitState.isRateLimited = true;
    rateLimitState.retryAt = Date.now() + (retryAfter || RATE_LIMIT_CONFIG.baseDelayMs);
    rateLimitState.lastError = appError;
  }

  // 控制台日志
  if (logToConsole) {
    console.warn(`[ErrorHandler] [${context}] ${errorMessage}`, { error, appError });
  }

  // 设置store错误状态
  setStoreError(appError);

  // 显示Toast通知（如果需要）
  if (showToast) {
    showErrorToast(appError);
  }

  return appError;
};

/**
 * 设置store错误状态
 */
const setStoreError = (error: AppError) => {
  const stores = [
    useMemoStore,
    useSectionStore,
    useGroupStore,
    useTagStore,
    useMemoObjectStore,
    useMemoIndexStore,
    useAppStore
  ];

  stores.forEach(store => {
    try {
      store.setState({ error: error.message });
    } catch (e) {
      console.warn(`Store does not have setState method`);
    }
  });
};

/**
 * 显示错误Toast通知
 */
const showErrorToast = (error: AppError) => {
  // 触发自定义事件，让UI层显示Toast
  window.dispatchEvent(new CustomEvent('app:error', { 
    detail: error 
  }));
  
  console.log(`[Toast] [${error.type}] ${error.message}`);
};

/**
 * 清除错误状态
 */
export const clearErrors = () => {
  const stores = [
    useMemoStore,
    useSectionStore,
    useGroupStore,
    useTagStore,
    useMemoObjectStore,
    useMemoIndexStore,
    useAppStore
  ];

  stores.forEach(store => {
    try {
      store.setState({ error: null });
    } catch (e) {
      console.warn(`Store does not have setState method`);
    }
  });

  // 清除429状态
  rateLimitState.isRateLimited = false;
  rateLimitState.retryAt = 0;
  rateLimitState.lastError = null;
};

/**
 * 检查是否有错误
 */
export const hasError = (): boolean => {
  const stores = [
    useMemoStore,
    useSectionStore,
    useGroupStore,
    useTagStore,
    useMemoObjectStore,
    useMemoIndexStore,
    useAppStore
  ];

  return stores.some(store => {
    try {
      const error = store.getState().error;
      return error && error.length > 0;
    } catch (e) {
      return false;
    }
  }) || rateLimitState.isRateLimited;
};

/**
 * 获取所有错误
 */
export const getAllErrors = (): string[] => {
  const stores = [
    useMemoStore,
    useSectionStore,
    useGroupStore,
    useTagStore,
    useMemoObjectStore,
    useMemoIndexStore,
    useAppStore
  ];

  const errors: string[] = [];
  stores.forEach(store => {
    try {
      const error = store.getState().error;
      if (error && error.length > 0) {
        errors.push(error);
      }
    } catch (e) {
      // ignore
    }
  });

  if (rateLimitState.isRateLimited && rateLimitState.lastError) {
    errors.push(rateLimitState.lastError.message);
  }

  return errors;
};

/**
 * 获取当前Rate Limit状态
 */
export const getRateLimitStatus = () => ({
  ...rateLimitState,
  canRetry: !rateLimitState.isRateLimited || Date.now() >= rateLimitState.retryAt,
  waitTimeRemaining: Math.max(0, rateLimitState.retryAt - Date.now())
});

/**
 * React Hook：使用错误处理
 */
export const useErrorHandler = () => {
  return {
    handleError,
    clearErrors,
    hasError,
    getAllErrors,
    isRateLimitError,
    getRateLimitWaitTime,
    getRateLimitStatus
  };
};
